import {
  CRITERION_VALUE,
  type CriterionValue,
} from "@/constants/criterion-values"
import {
  yesNoBadgeColorClasses,
  yesNoBadgeEmptyClasses,
} from "@/metrics/metric-display"

import type { TestCaseRecord } from "@/types/api/TestCaseRecord"

export type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

export interface CriterionResult {
  criterion: string
  result: CriterionValue
  explanation: string
  matching_message_indices: number[]
}

export interface CompositeEvaluationResultJson {
  reporting_method: string
  all_computed_values: {
    percentage_of_criteria_met: number
    all_criteria_must_be_met: boolean
    any_criteria_must_be_met: boolean
    count_of_criteria_met: number
    all_criteria_met?: number
  }
  summary: {
    criteria_met_count: number
    criteria_not_met_count: number
    criteria_unknown_count: number
    criteria_evaluated_count: number
    criteria_total_count: number
  }
  individual_results: CriterionResult[]
}

export interface CompositeResultData {
  summary: CompositeEvaluationResultJson["summary"]
  individual_results: CriterionResult[]
}

/**
 * Extract composite result data from the structured `result` field (new)
 * or fall back to `result_json` (old data).
 */
export function getCompositeResultData(
  metricOutput:
    | {
        result?: Record<string, unknown> | null
        result_json?: Record<string, unknown> | null
      }
    | undefined,
): CompositeResultData | undefined {
  const result = metricOutput?.result as CompositeResultData | undefined
  if (result?.summary && result?.individual_results) {
    return result
  }
  const resultJson = metricOutput?.result_json as
    | CompositeEvaluationResultJson
    | undefined
  if (resultJson?.summary && resultJson?.individual_results) {
    return resultJson
  }
  return undefined
}

export const isAllCriteriaMetPass = (
  summary: CompositeEvaluationResultJson["summary"],
): boolean =>
  summary.criteria_total_count > 0 &&
  summary.criteria_met_count === summary.criteria_total_count

export const CRITERION_DISPLAY: Record<
  CriterionValue,
  { label: string; color: keyof typeof yesNoBadgeColorClasses | "na" }
> = {
  [CRITERION_VALUE.MET]: { label: "Met", color: "success" },
  [CRITERION_VALUE.NOT_MET]: { label: "Not", color: "danger" },
  [CRITERION_VALUE.UNKNOWN]: { label: "N/A", color: "na" },
}

/**
 * The AI and Human columns are laid out on fixed widths rather than sized to
 * their content, so the judge-score chips in the card's top row can sit
 * directly above the columns they summarize. Both are right-anchored to the
 * card's content edge, so matching widths is what lines them up — and the
 * table needs `table-fixed` for these to be exact rather than minimums.
 *
 * A narrow card gets narrow judge columns, where the vote buttons stack
 * vertically, rather than letting two fixed columns squeeze the criteria text
 * to a few characters a line.
 */
export const COMPOSITE_AI_COLUMN_CLASS = "w-20 @md/card:w-32"
export const COMPOSITE_HUMAN_COLUMN_CLASS = "w-24 @md/card:w-64"

export const CRITERION_VALUES: CriterionValue[] = [
  CRITERION_VALUE.MET,
  CRITERION_VALUE.NOT_MET,
  CRITERION_VALUE.UNKNOWN,
]

export function getCriterionBadgeClass(val: CriterionValue): string {
  if (val === CRITERION_VALUE.MET) {
    return yesNoBadgeColorClasses.success
  }
  if (val === CRITERION_VALUE.NOT_MET) {
    return yesNoBadgeColorClasses.danger
  }
  return yesNoBadgeEmptyClasses
}

export function getCriterionSelectedButtonClass(val: CriterionValue): string {
  if (val === CRITERION_VALUE.MET) {
    return "bg-coval-success-surface text-coval-success-on-surface font-medium hover:bg-coval-success-surface hover:text-coval-success-on-surface"
  }
  if (val === CRITERION_VALUE.NOT_MET) {
    return "bg-coval-danger-surface text-coval-danger-on-surface font-medium hover:bg-coval-danger-surface hover:text-coval-danger-on-surface"
  }
  return "bg-surface-1 text-coval-text-muted font-medium hover:bg-surface-1 hover:text-coval-text-muted"
}

export function buildCriterionUpdatePayload(
  index: number,
  value: CriterionValue,
  currentSetValue: string[] | null | undefined,
  criteria: CriterionResult[],
  reportingMethod: string,
): {
  floatScore: number
  setTracker: string[]
  jsonValue: CompositeEvaluationResultJson
} {
  const setTracker = Array.from(
    { length: criteria.length },
    (_, i) => currentSetValue?.[i] ?? "",
  )
  setTracker[index] = setTracker[index] === value ? "" : value

  const currentResults: CriterionResult[] = criteria.map((c, i) => {
    const humanVal = setTracker[i]
    return {
      ...c,
      result:
        humanVal === CRITERION_VALUE.MET ||
        humanVal === CRITERION_VALUE.NOT_MET ||
        humanVal === CRITERION_VALUE.UNKNOWN
          ? humanVal
          : c.result,
    }
  })

  const metCount = setTracker.filter((v) => v === CRITERION_VALUE.MET).length
  const notMetCount = setTracker.filter(
    (v) => v === CRITERION_VALUE.NOT_MET,
  ).length
  const unknownCount = setTracker.filter(
    (v) => v === CRITERION_VALUE.UNKNOWN,
  ).length
  const evaluatedCount = metCount + notMetCount
  const decimal = evaluatedCount > 0 ? metCount / evaluatedCount : 0
  const pct = decimal * 100
  const allMet = metCount === criteria.length && criteria.length > 0 ? 1.0 : 0.0

  // float_value is the decimal fraction of met criteria (0–1), matching the BE writer
  // in composite_evaluation_metric.py. Raw counts live in `summary` for display surfaces.
  const floatScore = decimal

  const jsonValue: CompositeEvaluationResultJson = {
    reporting_method: reportingMethod,
    summary: {
      criteria_met_count: metCount,
      criteria_not_met_count: notMetCount,
      criteria_unknown_count: unknownCount,
      criteria_evaluated_count: evaluatedCount,
      criteria_total_count: criteria.length,
    },
    individual_results: currentResults,
    all_computed_values: {
      percentage_of_criteria_met: pct,
      all_criteria_met: allMet,
      count_of_criteria_met: metCount,
      any_criteria_must_be_met: metCount > 0,
      all_criteria_must_be_met: allMet === 1.0,
    },
  }

  return { floatScore, setTracker, jsonValue }
}
